import { createEffect, createSignal, untrack } from 'solid-js';
import { createStore } from 'solid-js/store';
import { createMagicDoorContext } from '~/contexts/utils';
import { OnboardingAiRepository } from '~/repositories/onboardingAiRepository';
import { createMutation } from '~/utils/resource';
import type { AIChatKey } from '~/components/Onboarding/V2/config';
import type { ChatHistoryMessage } from '~/repositories/onboardingAiRepository';

const repo = new OnboardingAiRepository();

const localStorageKey = 'playedMessagesCache';

export const [OnboardingAiProvider, useOnboardingAi] = createMagicDoorContext('OnboardingAi', () => {
  const [currentChatId, setCurrentChatId] = createSignal<string | undefined>(undefined);
  const [currentInjectionKey, setCurrentInjectionKey] = createSignal<AIChatKey | undefined>(undefined);
  const [isInitialized, setIsInitialized] = createSignal<boolean>(false);
  const [isMuted, setIsMuted] = createSignal<boolean>(false);

  const [currentAudio, setCurrentAudio] = createSignal<HTMLAudioElement | null>(null);
  const [loadingMessageId, setLoadingMessageId] = createSignal<string | null>(null);
  const [playingMessageId, setPlayingMessageId] = createSignal<string | null>(null);

  const [playedMessages, setPlayedMessages] = createStore<Record<string, number>>(
    JSON.parse(localStorage.getItem(localStorageKey) || '{}')
  );

  const [chatHistory, setChatHistory] = createSignal<{ messages: ChatHistoryMessage[] } | undefined>(undefined);

  const refetchChatHistory = createMutation(async () => {
    const _currentChatId = currentChatId();
    if (!_currentChatId) return undefined;
    const history = await repo.getChatHistory(_currentChatId);
    setChatHistory({ messages: history.reverse() });
  });

  const sendMessage = createMutation(async (params: { message: string }) => {
    const { message } = params;
    const chatId = currentChatId();
    if (!chatId) {
      return;
    }
    await repo.sendMessage(chatId, { message, formKey: currentInjectionKey() });
    await refetchChatHistory();
  });

  const injectMessage = createMutation(async (params: { key: AIChatKey }) => {
    const { key } = params;
    const chatId = currentChatId();
    if (!chatId) {
      return;
    }
    await repo.injectMessage(chatId, { key });
    await refetchChatHistory();
  });

  const getMessageVoice = createMutation(async (params: { messageId: string }) => {
    const { messageId } = params;
    const chatId = currentChatId();
    if (!chatId) {
      return undefined;
    }
    try {
      const blob = await repo.getMessageVoice(chatId, messageId);
      console.log('Message voice blob received:', blob);
      return blob;
    } catch (error) {
      console.error('Failed to get message voice:', error);
      throw error;
    }
  });

  const getPreRecordedVoice = createMutation(async (key: string) => {
    try {
      const blob = await repo.getPreRecordedVoice(key);
      console.log('Pre-recorded voice blob received:', blob);
      return blob;
    } catch (error) {
      console.error('Failed to get pre-recorded voice:', error);
      throw error;
    }
  });

  const getMessageId = (message: ChatHistoryMessage) => {
    return message.id || '';
  };

  const isMessagePlayed = (message: ChatHistoryMessage) => {
    if (message.injectionKey) return false;
    const messageId = getMessageId(message);
    const count = playedMessages[messageId] || 0;
    return count > 2;
  };

  const fetchAudioBlob = async (message: ChatHistoryMessage) => {
    const audioBlob = message.injectionKey
      ? await getPreRecordedVoice(message.injectionKey)
      : await getMessageVoice({ messageId: message.id! });

    if (audioBlob) {
      await playAudio(audioBlob, message.id!);
    }
  };

  const cleanupAudio = () => {
    const audio = currentAudio();
    if (audio) {
      audio.pause();
      URL.revokeObjectURL(audio.src);
    }
    setPlayingMessageId(null);

    setCurrentAudio(null);
  };

  const playAudio = async (audioBlob: Blob, messageId: string) => {
    // 如果已经静音，直接返回
    if (isMuted()) {
      return;
    }

    setPlayingMessageId(messageId);

    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    setCurrentAudio(audio);

    const onEnded = () => {
      cleanupAudio();
      setCurrentAudio(null);
      setLoadingMessageId(null);
    };

    audio.onended = onEnded;

    try {
      await audio.play();
      const currentCount = untrack(() => playedMessages[messageId] ?? 0);
      setPlayedMessages({
        [messageId]: currentCount + 1,
      });
    } catch {
      cleanupAudio();
    }
  };

  const playAudioMessage = async (message: ChatHistoryMessage) => {
    const messageId = getMessageId(message);
    if (!messageId || isMuted()) return;

    cleanupAudio();
    setLoadingMessageId(messageId);
    await fetchAudioBlob(message).finally(() => {
      setLoadingMessageId(null);
    });
  };

  const handleMuteToggle = () => {
    const newMutedState = !isMuted();
    setIsMuted(newMutedState);

    // 如果静音，停止当前播放的音频
    if (newMutedState) {
      cleanupAudio();
    }
  };

  const initChatByUUID = async (id: string) => {
    setCurrentChatId(id);
    try {
      await refetchChatHistory();
      setIsInitialized(true);
    } catch {
      setIsInitialized(false);
    }
  };

  createEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(playedMessages));
  });

  return {
    chatId: currentChatId,
    currentChatId,
    isInitialized,
    chatHistory,
    sendMessage,
    injectMessage,
    getMessageVoice,
    getPreRecordedVoice,
    isMuted,
    setIsMuted,
    handleMuteToggle,
    loadingMessageId,
    getMessageId,
    isMessagePlayed,
    playAudioMessage,
    cleanupAudio,
    initChatByUUID,
    playingMessageId,
    currentInjectionKey,
    setCurrentInjectionKey,
    playedMessages,
  };
});
